import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Key, Copy, Check, ArrowLeft, ExternalLink, Menu, X,
  Info, AlertTriangle, ChevronRight, BookOpen, Zap, Shield,
  Package, ShoppingCart, Bell, Code2, Terminal
} from "lucide-react";

interface CodeExampleProps {
  examples: { [key: string]: string };
  response?: string;
}

function CodeExample({ examples, response }: CodeExampleProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const languages = Object.keys(examples);

  const copyCode = (code: string, lang: string) => {
    navigator.clipboard.writeText(code);
    setCopied(lang);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="my-6">
      <Tabs defaultValue={languages[0]} className="w-full">
        <TabsList className="h-auto flex-wrap justify-start bg-slate-800 border-b border-slate-700 rounded-t-lg rounded-b-none p-1 gap-1">
          {languages.map((lang) => (
            <TabsTrigger
              key={lang}
              value={lang}
              className="text-xs px-3 py-1.5 data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 rounded"
            >
              {lang}
            </TabsTrigger>
          ))}
        </TabsList>
        {languages.map((lang) => (
          <TabsContent key={lang} value={lang} className="mt-0">
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-t-none rounded-b-lg text-sm overflow-x-auto font-mono leading-relaxed border border-t-0 border-slate-700">
                <code>{examples[lang]}</code>
              </pre>
              <button
                onClick={() => copyCode(examples[lang], lang)}
                className="absolute top-3 right-3 p-2 rounded bg-slate-700/80 hover:bg-slate-600 transition-colors"
                title="Copy code"
              >
                {copied === lang ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4 text-slate-300" />
                )}
              </button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
      {response && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-muted-foreground">Response</span>
          </div>
          <pre className="bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-4 rounded-lg text-sm overflow-x-auto font-mono border">
            <code>{response}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

function InfoBox({ children, variant = "info" }: { children: React.ReactNode; variant?: "info" | "warning" }) {
  return (
    <div className={`my-6 p-4 rounded-lg border-l-4 ${
      variant === "warning" 
        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-800 dark:text-amber-200" 
        : "bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-800 dark:text-blue-200"
    }`}>
      <div className="flex items-start gap-3">
        {variant === "warning" ? (
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        ) : (
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
        )}
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-green-100 text-green-700 border-green-300",
    POST: "bg-blue-100 text-blue-700 border-blue-300",
    PUT: "bg-amber-100 text-amber-700 border-amber-300",
    PATCH: "bg-purple-100 text-purple-700 border-purple-300",
    DELETE: "bg-red-100 text-red-700 border-red-300",
  };
  return (
    <Badge variant="outline" className={`font-mono font-semibold ${colors[method] || ""}`}>
      {method}
    </Badge>
  );
}

function ParamTable({ params }: { params: { name: string; type: string; required?: boolean; description: string }[] }) {
  return (
    <div className="my-4 overflow-x-auto">
      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>
            <th className="text-left px-4 py-3 font-semibold border-b">Parameter</th>
            <th className="text-left px-4 py-3 font-semibold border-b">Type</th>
            <th className="text-left px-4 py-3 font-semibold border-b">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((param, i) => (
            <tr key={param.name} className={i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/50 dark:bg-slate-800/50"}>
              <td className="px-4 py-3 border-b font-mono text-sm">
                {param.name}
                {param.required && <span className="text-red-500 ml-1">*</span>}
              </td>
              <td className="px-4 py-3 border-b text-muted-foreground">{param.type}</td>
              <td className="px-4 py-3 border-b">{param.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NAV_ITEMS = [
  { id: "getting-started", title: "Getting Started", icon: Zap },
  { id: "authentication", title: "Authentication", icon: Shield },
  { id: "rate-limits", title: "Rate Limits & Quotas", icon: Terminal },
  { id: "products", title: "Products", icon: Package },
  { id: "inventory", title: "Inventory", icon: Package },
  { id: "orders", title: "Orders", icon: ShoppingCart },
  { id: "webhooks", title: "Webhooks", icon: Bell },
  { id: "errors", title: "Error Handling", icon: AlertTriangle },
];

export default function ApiDocsPage() {
  const { user } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("getting-started");

  useEffect(() => {
    document.title = "API Documentation | OpenBox by ShopJimmy";
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-100px 0px -70% 0px" }
    );

    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileNavOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="lg:hidden p-2 -ml-2"
              data-testid="btn-mobile-nav"
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link href="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">OpenBox</span>
            </Link>
            <Separator orientation="vertical" className="h-6 hidden sm:block" />
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="font-semibold">API Documentation</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              v1
            </Badge>
            {user ? (
              <Link href="/api-access" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3" data-testid="btn-manage-keys">
                <Key className="h-4 w-4 mr-2" />
                Manage API Keys
              </Link>
            ) : (
              <Link href="/login?redirect=/api-access" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3" data-testid="btn-get-api-key">
                <Key className="h-4 w-4 mr-2" />
                Get API Key
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="container flex">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-14 left-0 z-40 w-64 border-r bg-background transform transition-transform lg:translate-x-0 lg:static lg:h-[calc(100vh-3.5rem)]
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <ScrollArea className="h-full py-6 px-4">
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">On this page</p>
            </div>
            <nav className="space-y-1">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.title}
                </button>
              ))}
            </nav>
            
            <Separator className="my-6" />
            
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Resources</p>
              {user ? (
                <Link href="/api-access" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground px-3 py-2">
                  <Key className="h-4 w-4" />
                  Your API Keys
                </Link>
              ) : (
                <Link href="/login?redirect=/api-access" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground px-3 py-2">
                  <Key className="h-4 w-4" />
                  Get API Key
                </Link>
              )}
              <a 
                href="mailto:jimmy@shopjimmy.com" 
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground px-3 py-2"
              >
                <ExternalLink className="h-4 w-4" />
                Contact Support
              </a>
            </div>
          </ScrollArea>
        </aside>

        {mobileNavOpen && (
          <div 
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-4 py-8 lg:px-12 lg:py-10 max-w-4xl">
          
          {/* Getting Started */}
          <section id="getting-started" className="scroll-mt-20 mb-16">
            <h1 className="text-3xl font-bold mb-4">OpenBox API</h1>
            <p className="text-lg text-muted-foreground mb-6">
              The OpenBox API enables programmatic access to our inventory catalog and order management system 
              for appliance resellers and retailers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="border rounded-lg p-4">
                <Package className="h-8 w-8 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Products & Inventory</h3>
                <p className="text-sm text-muted-foreground">Browse catalog and check real-time stock levels</p>
              </div>
              <div className="border rounded-lg p-4">
                <ShoppingCart className="h-8 w-8 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Order Management</h3>
                <p className="text-sm text-muted-foreground">Create and track orders programmatically</p>
              </div>
              <div className="border rounded-lg p-4">
                <Bell className="h-8 w-8 text-primary mb-2" />
                <h3 className="font-semibold mb-1">Webhooks</h3>
                <p className="text-sm text-muted-foreground">Get real-time notifications for events</p>
              </div>
            </div>

            <h2 className="text-xl font-semibold mb-4">Base URL</h2>
            <p className="text-muted-foreground mb-3">All API endpoints are relative to:</p>
            <code className="block bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-lg font-mono text-sm">
              https://openbox.shopjimmy.com/api/v1
            </code>

            <h2 className="text-xl font-semibold mt-8 mb-4">Quick Start</h2>
            <p className="text-muted-foreground mb-4">
              Make your first API request to list products:
            </p>
            
            <CodeExample
              examples={{
                "Node.js": `const response = await fetch('https://openbox.shopjimmy.com/api/v1/products', {
  headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
});
const { data, pagination } = await response.json();
console.log(\`Found \${pagination.total} products\`);`,
                "Python": `import requests

response = requests.get(
    'https://openbox.shopjimmy.com/api/v1/products',
    headers={'Authorization': 'Bearer YOUR_API_KEY'}
)
data = response.json()
print(f"Found {data['pagination']['total']} products")`,
                "PHP": `<?php
$ch = curl_init('https://openbox.shopjimmy.com/api/v1/products');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ['Authorization: Bearer YOUR_API_KEY']
]);
$response = json_decode(curl_exec($ch), true);
echo "Found " . $response['pagination']['total'] . " products";`,
                "Ruby": `require 'httparty'

response = HTTParty.get(
  'https://openbox.shopjimmy.com/api/v1/products',
  headers: { 'Authorization' => 'Bearer YOUR_API_KEY' }
)
puts "Found #{response['pagination']['total']} products"`,
                "Go": `req, _ := http.NewRequest("GET", "https://openbox.shopjimmy.com/api/v1/products", nil)
req.Header.Set("Authorization", "Bearer YOUR_API_KEY")
resp, _ := http.DefaultClient.Do(req)
defer resp.Body.Close()

var result map[string]interface{}
json.NewDecoder(resp.Body).Decode(&result)`,
                "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/products" \\
  -H "Authorization: Bearer YOUR_API_KEY"`
              }}
              response={`{
  "data": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "brand": "Samsung",
      "sku": "RF28R7551SR",
      "name": "28 cu. ft. 4-Door French Door Refrigerator",
      "msrp": 2799.99,
      "category": "Refrigerators"
    }
  ],
  "pagination": {
    "total": 245,
    "limit": 20,
    "offset": 0,
    "has_more": true
  }
}`}
            />
          </section>

          <Separator className="my-12" />

          {/* Authentication */}
          <section id="authentication" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Authentication</h2>
            <p className="text-muted-foreground mb-6">
              All API requests require authentication using an API key. Include your key in the 
              <code className="mx-1 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-sm">Authorization</code> 
              header.
            </p>

            <InfoBox>
              <strong>Store credentials securely.</strong> Always use environment variables to store your API key. 
              Never commit API keys to version control or expose them in client-side code.
            </InfoBox>

            <h3 className="text-lg font-semibold mt-8 mb-3">Header Authentication</h3>
            <p className="text-muted-foreground mb-4">Use Bearer token authentication:</p>

            <CodeExample
              examples={{
                "Node.js": `const headers = {
  'Authorization': \`Bearer \${process.env.OPENBOX_API_KEY}\`,
  'Content-Type': 'application/json'
};

const response = await fetch(url, { headers });`,
                "Python": `import os

headers = {
    'Authorization': f"Bearer {os.environ['OPENBOX_API_KEY']}",
    'Content-Type': 'application/json'
}

response = requests.get(url, headers=headers)`,
                "PHP": `<?php
$headers = [
    'Authorization: Bearer ' . getenv('OPENBOX_API_KEY'),
    'Content-Type: application/json'
];`,
                "Ruby": `headers = {
  'Authorization' => "Bearer #{ENV['OPENBOX_API_KEY']}",
  'Content-Type' => 'application/json'
}`,
                "Go": `req.Header.Set("Authorization", "Bearer "+os.Getenv("OPENBOX_API_KEY"))
req.Header.Set("Content-Type", "application/json")`,
                "cURL": `curl -H "Authorization: Bearer $OPENBOX_API_KEY" \\
     -H "Content-Type: application/json" \\
     https://openbox.shopjimmy.com/api/v1/products`
              }}
            />

            <h3 className="text-lg font-semibold mt-8 mb-3">Alternative: X-API-Key Header</h3>
            <p className="text-muted-foreground mb-4">You can also use the X-API-Key header:</p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono">
              X-API-Key: YOUR_API_KEY
            </pre>

            <h3 className="text-lg font-semibold mt-8 mb-3">Authentication Errors</h3>
            <ParamTable
              params={[
                { name: "401 Unauthorized", type: "Error", description: "Missing or invalid API key" },
                { name: "403 Forbidden", type: "Error", description: "API key is pending approval, rejected, or suspended" },
              ]}
            />
          </section>

          <Separator className="my-12" />

          {/* Rate Limits */}
          <section id="rate-limits" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Rate Limits & Quotas</h2>
            <p className="text-muted-foreground mb-6">
              The API uses rate limiting to ensure fair usage and system stability.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border rounded-lg p-4 bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold">Inventory Requests</h3>
                </div>
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">1,000/month</p>
                <p className="text-sm text-muted-foreground mt-1">Products and inventory browsing</p>
              </div>
              <div className="border rounded-lg p-4 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold">Order Requests</h3>
                </div>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">Unlimited</p>
                <p className="text-sm text-muted-foreground mt-1">Order creation and retrieval</p>
              </div>
            </div>

            <h3 className="text-lg font-semibold mt-8 mb-3">Rate Limit Headers</h3>
            <p className="text-muted-foreground mb-4">Every response includes usage information:</p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono mb-6">
{`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 847
X-RateLimit-Reset: 2024-02-01T00:00:00Z`}
            </pre>

            <InfoBox variant="warning">
              <strong>Quota exceeded?</strong> When you exceed your monthly quota, you'll receive a 
              <code className="mx-1 px-1 bg-amber-100 dark:bg-amber-800 rounded">429 Too Many Requests</code> 
              response. Contact us to discuss higher limits.
            </InfoBox>

            <h3 className="text-lg font-semibold mt-8 mb-3">Handling Rate Limits</h3>
            <CodeExample
              examples={{
                "Node.js": `async function fetchWithRateLimit(url, options) {
  const response = await fetch(url, options);
  
  const remaining = parseInt(response.headers.get('X-RateLimit-Remaining'));
  if (remaining < 100) {
    console.warn(\`Low quota: \${remaining} requests remaining\`);
  }
  
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After') || 60;
    await new Promise(r => setTimeout(r, retryAfter * 1000));
    return fetchWithRateLimit(url, options);
  }
  
  return response;
}`,
                "Python": `def request_with_retry(method, url, **kwargs):
    response = requests.request(method, url, **kwargs)
    
    remaining = int(response.headers.get('X-RateLimit-Remaining', 1000))
    if remaining < 100:
        print(f"Warning: Only {remaining} requests remaining")
    
    if response.status_code == 429:
        retry_after = int(response.headers.get('Retry-After', 60))
        time.sleep(retry_after)
        return request_with_retry(method, url, **kwargs)
    
    return response`,
                "cURL": `# Check headers in response
curl -i -H "Authorization: Bearer $API_KEY" \\
  https://openbox.shopjimmy.com/api/v1/products`
              }}
            />
          </section>

          <Separator className="my-12" />

          {/* Products */}
          <section id="products" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Products</h2>
            <p className="text-muted-foreground mb-6">
              Browse the product catalog with filtering and pagination.
            </p>

            {/* List Products */}
            <div className="border rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/products</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Retrieve a paginated list of all products in the catalog.
              </p>

              <h4 className="font-semibold mb-3">Query Parameters</h4>
              <ParamTable
                params={[
                  { name: "limit", type: "integer", description: "Results per page (1-100, default: 20)" },
                  { name: "offset", type: "integer", description: "Number of results to skip" },
                  { name: "category", type: "string", description: "Filter by category (e.g., Refrigerators, TVs)" },
                  { name: "brand", type: "string", description: "Filter by brand name" },
                  { name: "search", type: "string", description: "Search brand, SKU, or description" },
                  { name: "min_price", type: "number", description: "Minimum MSRP filter" },
                  { name: "max_price", type: "number", description: "Maximum MSRP filter" },
                  { name: "sort_by", type: "string", description: "Sort field: created_at, brand, msrp, name" },
                  { name: "sort_order", type: "string", description: "asc or desc (default: desc)" },
                ]}
              />

              <CodeExample
                examples={{
                  "Node.js": `// List refrigerators sorted by price
const params = new URLSearchParams({
  category: 'Refrigerators',
  sort_by: 'msrp',
  sort_order: 'asc',
  limit: '50'
});

const response = await fetch(
  \`https://openbox.shopjimmy.com/api/v1/products?\${params}\`,
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
);
const { data, pagination } = await response.json();`,
                  "Python": `# List refrigerators sorted by price
response = requests.get(
    'https://openbox.shopjimmy.com/api/v1/products',
    headers={'Authorization': f'Bearer {API_KEY}'},
    params={
        'category': 'Refrigerators',
        'sort_by': 'msrp',
        'sort_order': 'asc',
        'limit': 50
    }
)
data = response.json()`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/products?category=Refrigerators&sort_by=msrp&limit=50" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
                response={`{
  "data": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "brand": "Samsung",
      "sku": "RF28R7551SR",
      "name": "28 cu. ft. 4-Door French Door Refrigerator",
      "color": "Stainless Steel",
      "msrp": 2799.99,
      "image_url": "https://storage.example.com/images/RF28R7551SR.jpg",
      "dimensions": { "width": 35.75, "depth": 36.5, "height": 70 },
      "weight_lbs": 350,
      "category": "Refrigerators",
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "total": 245,
    "limit": 50,
    "offset": 0,
    "has_more": true
  }
}`}
              />
            </div>

            {/* Get Product */}
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/products/:id</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Retrieve detailed information about a specific product.
              </p>

              <CodeExample
                examples={{
                  "Node.js": `const productId = '550e8400-e29b-41d4-a716-446655440000';
const response = await fetch(
  \`https://openbox.shopjimmy.com/api/v1/products/\${productId}\`,
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
);

if (response.status === 404) {
  console.log('Product not found');
} else {
  const product = await response.json();
  console.log(\`\${product.brand} \${product.name}\`);
}`,
                  "Python": `product_id = '550e8400-e29b-41d4-a716-446655440000'
response = requests.get(
    f'https://openbox.shopjimmy.com/api/v1/products/{product_id}',
    headers={'Authorization': f'Bearer {API_KEY}'}
)

if response.status_code == 404:
    print('Product not found')
else:
    product = response.json()
    print(f"{product['brand']} {product['name']}")`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/products/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
              />
            </div>
          </section>

          <Separator className="my-12" />

          {/* Inventory */}
          <section id="inventory" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Inventory</h2>
            <p className="text-muted-foreground mb-6">
              Check real-time stock levels across warehouses with grade and pricing information.
            </p>

            <h3 className="text-lg font-semibold mb-3">Inventory Grades</h3>
            <ParamTable
              params={[
                { name: "A", type: "Grade", description: "New, factory-sealed condition" },
                { name: "B", type: "Grade", description: "Open-box, like-new with individual unit tracking" },
                { name: "C", type: "Grade", description: "Refurbished, functional with minor cosmetic issues" },
                { name: "D", type: "Grade", description: "As-is, sold for parts or repair" },
              ]}
            />

            {/* List Inventory */}
            <div className="border rounded-lg p-6 mt-8 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/inventory</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Retrieve inventory availability across all products.
              </p>

              <h4 className="font-semibold mb-3">Query Parameters</h4>
              <ParamTable
                params={[
                  { name: "product_id", type: "uuid", description: "Filter by specific product" },
                  { name: "grade", type: "string", description: "Filter by grade: A, B, C, D" },
                  { name: "in_stock", type: "boolean", description: "Only show available items (true)" },
                  { name: "limit", type: "integer", description: "Results per page (1-100)" },
                  { name: "offset", type: "integer", description: "Number of results to skip" },
                ]}
              />

              <CodeExample
                examples={{
                  "Node.js": `// Get all in-stock A-grade inventory
const response = await fetch(
  'https://openbox.shopjimmy.com/api/v1/inventory?grade=A&in_stock=true',
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
);
const { data } = await response.json();

data.forEach(item => {
  console.log(\`\${item.grade} grade at \${item.warehouse.name}: $\${item.selling_price}\`);
});`,
                  "Python": `# Get all in-stock A-grade inventory
response = requests.get(
    'https://openbox.shopjimmy.com/api/v1/inventory',
    headers={'Authorization': f'Bearer {API_KEY}'},
    params={'grade': 'A', 'in_stock': 'true'}
)

for inv_item in response.json()['data']:
    print(f"{inv_item['grade']} grade at {inv_item['warehouse']['name']}: \${inv_item['selling_price']}")`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/inventory?grade=A&in_stock=true" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
                response={`{
  "data": [
    {
      "id": "inv-001",
      "product_id": "550e8400-e29b-41d4-a716-446655440000",
      "grade": "A",
      "stock": { "quantity": 5, "serialized_units": { "total": 0, "available": 0 } },
      "selling_price": 1899.99,
      "promo_price": 1799.99,
      "warehouse": { "name": "Minneapolis DC", "city": "Minneapolis", "state": "MN" },
      "inventory_status": "in-stock",
      "is_available": true
    }
  ],
  "pagination": { "total": 89, "limit": 20, "offset": 0, "has_more": true }
}`}
              />
            </div>

            {/* Get Product Inventory */}
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/inventory/:productId</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Get all inventory locations and grades for a specific product.
              </p>

              <CodeExample
                examples={{
                  "Node.js": `// Find best price for a product
const productId = '550e8400-e29b-41d4-a716-446655440000';
const response = await fetch(
  \`https://openbox.shopjimmy.com/api/v1/inventory/\${productId}\`,
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
);

const { inventory } = await response.json();
const available = inventory.filter(i => i.is_available);
const cheapest = available.sort((a, b) => a.selling_price - b.selling_price)[0];

console.log(\`Best price: $\${cheapest.selling_price} at \${cheapest.warehouse.name}\`);`,
                  "Python": `# Find best price for a product
product_id = '550e8400-e29b-41d4-a716-446655440000'
response = requests.get(
    f'https://openbox.shopjimmy.com/api/v1/inventory/{product_id}',
    headers={'Authorization': f'Bearer {API_KEY}'}
)

inventory = response.json()['inventory']
available = [i for i in inventory if i['is_available']]
best_price = min(available, key=lambda x: x['selling_price'])

print(f"Best price: \${best_price['selling_price']} at {best_price['warehouse']['name']}")`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/inventory/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
              />
            </div>
          </section>

          <Separator className="my-12" />

          {/* Orders */}
          <section id="orders" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Orders</h2>
            <p className="text-muted-foreground mb-6">
              Create and manage orders programmatically.
            </p>

            {/* Create Order */}
            <div className="border rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="POST" />
                <code className="text-lg font-mono">/orders</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Place a new order. Orders are processed automatically.
              </p>

              <h4 className="font-semibold mb-3">Request Body</h4>
              <ParamTable
                params={[
                  { name: "items", type: "array", required: true, description: "Array of inventory items to order" },
                  { name: "items[].inventory_id", type: "uuid", required: true, description: "Inventory record ID" },
                  { name: "items[].quantity", type: "integer", description: "Quantity (default: 1)" },
                  { name: "items[].serialized_unit_id", type: "uuid", description: "Specific Serialized Unit ID (legacy alias `b_grade_unit_id` also accepted)" },
                  { name: "shipping_address", type: "object", required: true, description: "Delivery address" },
                  { name: "po_number", type: "string", description: "Your purchase order number" },
                  { name: "notes", type: "string", description: "Special delivery instructions" },
                  { name: "shipping_method", type: "string", description: "FREIGHT or LOCAL_PICKUP" },
                ]}
              />

              <CodeExample
                examples={{
                  "Node.js": `const order = await fetch('https://openbox.shopjimmy.com/api/v1/orders', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    items: [
      { inventory_id: 'inv-001', quantity: 1 },
      { inventory_id: 'inv-002', quantity: 1, serialized_unit_id: 'unit-001' }
    ],
    shipping_address: {
      name: 'John Smith',
      company: 'Acme Corp',
      street1: '123 Main St',
      street2: 'Suite 100',
      city: 'Minneapolis',
      state: 'MN',
      postal_code: '55401',
      country: 'US',
      phone: '612-555-1234',
      email: 'john@example.com'
    },
    po_number: 'PO-2024-001',
    notes: 'Please call before delivery',
    shipping_method: 'FREIGHT'
  })
}).then(r => r.json());

if (order.is_unfillable) {
  console.warn('Some items unavailable:', order.unfillable_items);
} else {
  console.log(\`Order \${order.order_number} created\`);
}`,
                  "Python": `response = requests.post(
    'https://openbox.shopjimmy.com/api/v1/orders',
    headers={
        'Authorization': f'Bearer {API_KEY}',
        'Content-Type': 'application/json'
    },
    json={
        'items': [
            {'inventory_id': 'inv-001', 'quantity': 1},
            {'inventory_id': 'inv-002', 'quantity': 1, 'serialized_unit_id': 'unit-001'}
        ],
        'shipping_address': {
            'name': 'John Smith',
            'company': 'Acme Corp',
            'street1': '123 Main St',
            'city': 'Minneapolis',
            'state': 'MN',
            'postal_code': '55401',
            'country': 'US',
            'email': 'john@example.com',
            'phone': '612-555-1234'
        },
        'po_number': 'PO-2024-001',
        'shipping_method': 'FREIGHT'
    }
)

order = response.json()
if order.get('is_unfillable'):
    print(f"Warning: Unfillable items: {order['unfillable_items']}")
else:
    print(f"Order {order['order_number']} created")`,
                  "cURL": `curl -X POST "https://openbox.shopjimmy.com/api/v1/orders" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "items": [{"inventory_id": "inv-001", "quantity": 1}],
    "shipping_address": {
      "name": "John Smith",
      "street1": "123 Main St",
      "city": "Minneapolis",
      "state": "MN",
      "postal_code": "55401",
      "email": "john@example.com"
    },
    "po_number": "PO-2024-001"
  }'`
                }}
                response={`{
  "id": "order-001",
  "order_number": "ORD-2024-0001",
  "status": "placed",
  "is_unfillable": false,
  "items": [
    {
      "inventory_id": "inv-001",
      "quantity": 1,
      "price": 1899.99,
      "grade": "A"
    }
  ],
  "subtotal": 1899.99,
  "shipping_method": "FREIGHT",
  "created_at": "2024-01-20T10:00:00Z"
}`}
              />

              <InfoBox>
                <strong>Unfillable Orders:</strong> If items are unavailable when the order is placed, 
                the order will be created with <code className="px-1 bg-blue-100 dark:bg-blue-800 rounded">status: "unfillable"</code>. 
                You'll receive a webhook notification and can swap items via the API.
              </InfoBox>
            </div>

            {/* List Orders */}
            <div className="border rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/orders</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Retrieve your order history with filtering options.
              </p>

              <h4 className="font-semibold mb-3">Order Statuses</h4>
              <ParamTable
                params={[
                  { name: "placed", type: "Status", description: "Order received, awaiting processing" },
                  { name: "unfillable", type: "Status", description: "Items unavailable, awaiting resolution" },
                  { name: "processing", type: "Status", description: "Order being prepared" },
                  { name: "shipped", type: "Status", description: "Order in transit" },
                  { name: "delivered", type: "Status", description: "Order delivered" },
                  { name: "cancelled", type: "Status", description: "Order cancelled" },
                ]}
              />

              <CodeExample
                examples={{
                  "Node.js": `// Get orders from last 30 days
const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
const response = await fetch(
  \`https://openbox.shopjimmy.com/api/v1/orders?since=\${since}&limit=50\`,
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
);

const { data } = await response.json();
const unfillable = data.filter(o => o.status === 'unfillable');
console.log(\`\${unfillable.length} orders need attention\`);`,
                  "Python": `from datetime import datetime, timedelta

# Get orders from last 30 days
since = (datetime.now() - timedelta(days=30)).isoformat()
response = requests.get(
    'https://openbox.shopjimmy.com/api/v1/orders',
    headers={'Authorization': f'Bearer {API_KEY}'},
    params={'since': since, 'limit': 50}
)

data = response.json()['data']
unfillable = [o for o in data if o['status'] == 'unfillable']
print(f"{len(unfillable)} orders need attention")`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/orders?status=unfillable" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
              />
            </div>

            {/* Get Order */}
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="GET" />
                <code className="text-lg font-mono">/orders/:id</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Retrieve detailed order information including tracking.
              </p>

              <CodeExample
                examples={{
                  "Node.js": `const order = await fetch(
  \`https://openbox.shopjimmy.com/api/v1/orders/\${orderId}\`,
  { headers: { 'Authorization': \`Bearer \${API_KEY}\` } }
).then(r => r.json());

console.log(\`Order \${order.order_number}: \${order.status}\`);

if (order.tracking) {
  console.log(\`Tracking: \${order.tracking.carrier} - \${order.tracking.number}\`);
}`,
                  "Python": `response = requests.get(
    f'https://openbox.shopjimmy.com/api/v1/orders/{order_id}',
    headers={'Authorization': f'Bearer {API_KEY}'}
)

order = response.json()
print(f"Order {order['order_number']}: {order['status']}")

if order.get('tracking'):
    print(f"Tracking: {order['tracking']['carrier']} - {order['tracking']['number']}")`,
                  "cURL": `curl -X GET "https://openbox.shopjimmy.com/api/v1/orders/order-001" \\
  -H "Authorization: Bearer $API_KEY"`
                }}
              />
            </div>
          </section>

          <Separator className="my-12" />

          {/* Webhooks */}
          <section id="webhooks" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Webhooks</h2>
            <p className="text-muted-foreground mb-6">
              Receive real-time notifications when events occur in your account.
            </p>

            <h3 className="text-lg font-semibold mb-3">Event Types</h3>
            <ParamTable
              params={[
                { name: "order.created", type: "Event", description: "New order placed successfully" },
                { name: "order.paid", type: "Event", description: "Payment received and confirmed" },
                { name: "order.shipped", type: "Event", description: "Order shipped with tracking info" },
                { name: "order.delivered", type: "Event", description: "Order delivered to customer" },
                { name: "order.cancelled", type: "Event", description: "Order was cancelled" },
                { name: "order.unfillable", type: "Event", description: "Order has unavailable items" },
                { name: "order.item_swapped", type: "Event", description: "Unfillable item was replaced" },
              ]}
            />

            {/* Register Webhook */}
            <div className="border rounded-lg p-6 mt-8 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <MethodBadge method="POST" />
                <code className="text-lg font-mono">/webhooks</code>
              </div>
              <p className="text-muted-foreground mb-4">
                Register a new webhook endpoint.
              </p>

              <CodeExample
                examples={{
                  "Node.js": `const webhook = await fetch('https://openbox.shopjimmy.com/api/v1/webhooks', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    webhook_url: 'https://mystore.com/webhooks/openbox',
    event_types: ['order.created', 'order.shipped', 'order.unfillable'],
    secret: 'my-webhook-secret-at-least-16-chars'
  })
}).then(r => r.json());

console.log(\`Webhook registered: \${webhook.id}\`);`,
                  "Python": `response = requests.post(
    'https://openbox.shopjimmy.com/api/v1/webhooks',
    headers={
        'Authorization': f'Bearer {API_KEY}',
        'Content-Type': 'application/json'
    },
    json={
        'webhook_url': 'https://mystore.com/webhooks/openbox',
        'event_types': ['order.created', 'order.shipped', 'order.unfillable'],
        'secret': 'my-webhook-secret-at-least-16-chars'
    }
)

webhook = response.json()
print(f"Webhook registered: {webhook['id']}")`,
                  "cURL": `curl -X POST "https://openbox.shopjimmy.com/api/v1/webhooks" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "webhook_url": "https://mystore.com/webhooks/openbox",
    "event_types": ["order.created", "order.shipped"],
    "secret": "my-webhook-secret-at-least-16-chars"
  }'`
                }}
              />
            </div>

            <h3 className="text-lg font-semibold mb-3">Webhook Security</h3>
            <p className="text-muted-foreground mb-4">
              All webhook payloads include an HMAC-SHA256 signature for verification:
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono mb-6">
{`X-OpenBox-Signature: sha256=abc123...
X-OpenBox-Timestamp: 1705764000`}
            </pre>

            <h4 className="font-semibold mb-3">Verifying Signatures</h4>
            <CodeExample
              examples={{
                "Node.js": `const crypto = require('crypto');

function verifyWebhookSignature(payload, signature, timestamp, secret) {
  // Reject if timestamp is too old (prevent replay attacks)
  const now = Math.floor(Date.now() / 1000);
  if (Math.abs(now - parseInt(timestamp)) > 300) {
    return false;
  }

  const signedPayload = timestamp + '.' + payload;
  const expectedSig = crypto
    .createHmac('sha256', secret)
    .update(signedPayload)
    .digest('hex');
  
  return crypto.timingSafeEqual(
    Buffer.from(expectedSig),
    Buffer.from(signature.replace('sha256=', ''))
  );
}

// Express middleware
app.post('/webhooks/openbox', express.raw({type: 'application/json'}), (req, res) => {
  const sig = req.headers['x-openbox-signature'];
  const ts = req.headers['x-openbox-timestamp'];
  
  if (!verifyWebhookSignature(req.body.toString(), sig, ts, WEBHOOK_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  const event = JSON.parse(req.body);
  console.log(\`Received: \${event.event}\`);
  res.json({ received: true });
});`,
                "Python": `import hmac
import hashlib
import time

def verify_webhook(payload, signature, timestamp, secret):
    # Reject if timestamp is too old
    if abs(time.time() - int(timestamp)) > 300:
        return False
    
    signed_payload = f"{timestamp}.{payload}"
    expected = hmac.new(
        secret.encode(),
        signed_payload.encode(),
        hashlib.sha256
    ).hexdigest()
    
    provided = signature.replace('sha256=', '')
    return hmac.compare_digest(expected, provided)

# Flask route
@app.route('/webhooks/openbox', methods=['POST'])
def handle_webhook():
    sig = request.headers.get('X-OpenBox-Signature', '')
    ts = request.headers.get('X-OpenBox-Timestamp', '')
    
    if not verify_webhook(request.data.decode(), sig, ts, WEBHOOK_SECRET):
        return {'error': 'Invalid signature'}, 401
    
    event = request.json
    print(f"Received: {event['event']}")
    return {'received': True}`,
                "PHP": `<?php
function verifyWebhook($payload, $signature, $timestamp, $secret) {
    if (abs(time() - intval($timestamp)) > 300) {
        return false;
    }
    
    $signedPayload = $timestamp . '.' . $payload;
    $expected = hash_hmac('sha256', $signedPayload, $secret);
    $provided = str_replace('sha256=', '', $signature);
    
    return hash_equals($expected, $provided);
}

$payload = file_get_contents('php://input');
$sig = $_SERVER['HTTP_X_OPENBOX_SIGNATURE'] ?? '';
$ts = $_SERVER['HTTP_X_OPENBOX_TIMESTAMP'] ?? '';

if (!verifyWebhook($payload, $sig, $ts, WEBHOOK_SECRET)) {
    http_response_code(401);
    exit(json_encode(['error' => 'Invalid signature']));
}

$event = json_decode($payload, true);
error_log("Received: " . $event['event']);
echo json_encode(['received' => true]);`
              }}
            />

            <h4 className="font-semibold mt-8 mb-3">Webhook Payload Example</h4>
            <pre className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg text-sm font-mono border">
{`{
  "event": "order.shipped",
  "timestamp": "2024-01-22T14:30:00Z",
  "data": {
    "order_id": "order-001",
    "order_number": "ORD-2024-0001",
    "tracking": {
      "carrier": "XPO Logistics",
      "number": "12345678901234"
    }
  }
}`}
            </pre>
          </section>

          <Separator className="my-12" />

          {/* Errors */}
          <section id="errors" className="scroll-mt-20 mb-16">
            <h2 className="text-2xl font-bold mb-4">Error Handling</h2>
            <p className="text-muted-foreground mb-6">
              All API errors return a consistent JSON structure for easy handling.
            </p>

            <h3 className="text-lg font-semibold mb-3">Error Response Format</h3>
            <pre className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg text-sm font-mono border mb-6">
{`{
  "error": "error_code",
  "message": "Human-readable error message",
  "details": {
    "field": "Additional context"
  }
}`}
            </pre>

            <h3 className="text-lg font-semibold mb-3">Error Codes</h3>
            <ParamTable
              params={[
                { name: "401 unauthorized", type: "Error", description: "Missing or invalid API key" },
                { name: "403 forbidden", type: "Error", description: "API key pending, rejected, or suspended" },
                { name: "404 not_found", type: "Error", description: "Resource not found" },
                { name: "400 validation_error", type: "Error", description: "Invalid request data" },
                { name: "409 conflict", type: "Error", description: "Resource already exists" },
                { name: "429 rate_limited", type: "Error", description: "Too many requests" },
                { name: "429 quota_exceeded", type: "Error", description: "Monthly quota exceeded" },
                { name: "500 internal_error", type: "Error", description: "Server error" },
              ]}
            />

            <h3 className="text-lg font-semibold mt-8 mb-3">Error Handling Example</h3>
            <CodeExample
              examples={{
                "Node.js": `class OpenBoxAPIError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details || {};
  }
}

async function apiRequest(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': \`Bearer \${API_KEY}\`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new OpenBoxAPIError(response.status, data.error, data.message, data.details);
  }

  return data;
}

// Usage
try {
  const products = await apiRequest('https://openbox.shopjimmy.com/api/v1/products');
} catch (error) {
  if (error.code === 'unauthorized') {
    console.error('Check your API key');
  } else if (error.code === 'quota_exceeded') {
    console.error('Contact support for higher limits');
  }
}`,
                "Python": `class OpenBoxAPIError(Exception):
    def __init__(self, status, code, message, details=None):
        self.status = status
        self.code = code
        self.details = details or {}
        super().__init__(message)

def api_request(method, url, **kwargs):
    response = requests.request(
        method, url,
        headers={'Authorization': f'Bearer {API_KEY}'},
        **kwargs
    )
    data = response.json()
    
    if not response.ok:
        raise OpenBoxAPIError(
            response.status_code,
            data.get('error'),
            data.get('message'),
            data.get('details')
        )
    
    return data

# Usage
try:
    products = api_request('GET', 'https://openbox.shopjimmy.com/api/v1/products')
except OpenBoxAPIError as e:
    if e.code == 'unauthorized':
        print('Check your API key')
    elif e.code == 'quota_exceeded':
        print('Contact support for higher limits')`
              }}
            />
          </section>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t text-center">
            <p className="text-muted-foreground mb-2">Need help with your integration?</p>
            <p className="text-sm">
              Contact Jimmy at{" "}
              <a href="mailto:jimmy@shopjimmy.com" className="text-primary hover:underline">
                jimmy@shopjimmy.com
              </a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
